import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Bot,
  Calendar,
  Search,
  User,
  LogOut,
  Trash2,
  ChevronDown,
  Settings,
  ArrowLeftRight,
} from 'lucide-react';
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react';
import { useHeader, useAuth } from '@/contexts';
import {
  converterPeriodoParaDatas,
  formatCNPJ,
  getPeriodoUltimosNDias,
  type PeriodoFiltro,
} from '@/lib/utils/formatters';
import {
  buildEmpresaDropdownItems,
  matchesEmpresaSearch,
  sortContribuintesForDropdown,
} from '@/lib/utils/contribuintes';
import { cn } from '@/lib/utils';
import { PeriodoModal } from './PeriodoModal';
import { GestorSwitchModal } from './GestorSwitchModal';
import { useListaContribuintes } from '@/services/Contribuintes/hooks/useListaContribuintes';
import { useBuscarStatusRobo } from '@/services/Relatorios/hooks/useBuscarStatusRobo';

interface AppHeaderProps {
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  onBuscar?: () => void;
  onLimparFiltros?: () => void;
  disableModoGrupoEmpresa?: boolean;
  disableFiltroPeriodo?: boolean;
  compact?: boolean;
  hideLogo?: boolean;
  snac?: string;
  showPeriodo?: boolean;
  defaultPeriodoModo?: 'periodo' | 'mesano';
}

export function AppHeader({
  title,
  subtitle,
  onBuscar,
  onLimparFiltros,
  disableModoGrupoEmpresa = false,
  disableFiltroPeriodo = false,
  compact = false,
  hideLogo = false,
  snac,
  showPeriodo = false,
  defaultPeriodoModo = 'periodo',
}: AppHeaderProps) {
  const { filtros, setEmpresa, setEmpresaCnpjs, setFiltros, limparFiltros, marcarAplicado } =
    useHeader();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRelatorios = location.pathname === '/relatorios';
  const { data: statusRobo, isLoading: isLoadingRobo } = useBuscarStatusRobo();
  const botAtivo = statusRobo?.StatusRobo === 'Ativo';
  const [cnpjSelecionado, setCnpjSelecionado] = useState(filtros.empresaId || '');
  const [showPeriodoModal, setShowPeriodoModal] = useState(false);
  const [showGestorModal, setShowGestorModal] = useState(false);
  const [searchEmpresa, setSearchEmpresa] = useState('');
  const [modoGruposEmpresa, setModoGruposEmpresa] = useState(false);
  const [gruposEmpresaAbertos, setGruposEmpresaAbertos] = useState<Record<string, boolean>>({});
  const [empresaSelectOpen, setEmpresaSelectOpen] = useState(false);
  const [periodoFiltro, setPeriodoFiltro] = useState<PeriodoFiltro>({
    modo: defaultPeriodoModo,
    mes: [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ][new Date().getMonth()],
    ano: new Date().getFullYear(),
    dataInicio: filtros.dataInicio || getPeriodoUltimosNDias(30).dataInicio,
    dataFim: filtros.dataFim || getPeriodoUltimosNDias(30).dataFim,
    referente: 'emissao',
  });

  useEffect(() => {
    setCnpjSelecionado(filtros.empresaId || '');
  }, [filtros.empresaId]);

  useEffect(() => {
    if (filtros.dataInicio || filtros.dataFim) {
      setPeriodoFiltro((prev: PeriodoFiltro) => ({
        ...prev,
        dataInicio: filtros.dataInicio || '',
        dataFim: filtros.dataFim || '',
      }));
    }
  }, [filtros.dataInicio, filtros.dataFim]);

  const { data: contribuintes, isLoading: isLoadingContribuintes } = useListaContribuintes(snac);

  const contribuintesOrdenados = useMemo(() => {
    return sortContribuintesForDropdown(contribuintes);
  }, [contribuintes]);

  // Filtrar contribuintes por nome ou CNPJ
  const contribuintesFiltrados = useMemo(() => {
    return contribuintesOrdenados.filter(contribuinte =>
      matchesEmpresaSearch(contribuinte, searchEmpresa)
    );
  }, [contribuintesOrdenados, searchEmpresa]);

  const dropdownEmpresasAgrupadas = useMemo(() => {
    return buildEmpresaDropdownItems(contribuintesOrdenados);
  }, [contribuintesOrdenados]);

  const dropdownEmpresasAgrupadasFiltradas = useMemo(() => {
    if (!searchEmpresa) return dropdownEmpresasAgrupadas;
    return dropdownEmpresasAgrupadas.filter(item => {
      if (item.kind === 'single') return matchesEmpresaSearch(item.empresa, searchEmpresa);
      return (
        matchesEmpresaSearch(item.matriz, searchEmpresa) ||
        item.vinculadas.some(v => matchesEmpresaSearch(v, searchEmpresa))
      );
    });
  }, [dropdownEmpresasAgrupadas, searchEmpresa]);

  const handlePeriodoChange = (filtro: PeriodoFiltro) => {
    setPeriodoFiltro(filtro);
    const { dataInicio, dataFim } = converterPeriodoParaDatas(filtro);
    setFiltros({
      dataInicio,
      dataFim,
      periodoModo: filtro?.modo === 'mesano' ? 'mesano' : 'periodo',
      periodoMes: filtro?.modo === 'mesano' ? (filtro?.mes ?? null) : null,
      periodoAno: filtro?.modo === 'mesano' && typeof filtro?.ano === 'number' ? filtro.ano : null,
    });
  };

  const handleEmpresaChange = (value: string) => {
    if (value === cnpjSelecionado) {
      setCnpjSelecionado('');
      setEmpresa('');
      setEmpresaCnpjs(null);
    } else {
      setCnpjSelecionado(value);
      setEmpresa(value);
      setEmpresaCnpjs(value);
    }
  };

  const handleGrupoChange = (matrizCnpj: string, cnpjsCsv: string) => {
    if (matrizCnpj === cnpjSelecionado) {
      setCnpjSelecionado('');
      setEmpresa('');
      setEmpresaCnpjs(null);
      return;
    }

    setCnpjSelecionado(matrizCnpj);
    setEmpresa(matrizCnpj);
    setEmpresaCnpjs(cnpjsCsv);
  };

  const handleLimparFiltros = () => {
    const periodoPadrao = getPeriodoUltimosNDias(30);
    setCnpjSelecionado('');
    setSearchEmpresa('');
    setPeriodoFiltro({
      modo: defaultPeriodoModo,
      mes: [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
      ][new Date().getMonth()],
      ano: new Date().getFullYear(),
      dataInicio: periodoPadrao.dataInicio,
      dataFim: periodoPadrao.dataFim,
      referente: 'emissao',
    });
    limparFiltros();
    onLimparFiltros?.();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const hasFiltrosAtivos = !!filtros.empresaId || !!filtros.dataInicio || !!filtros.dataFim;
  const isBuscaDisabled = modoGruposEmpresa && !filtros.empresaId;
  const buscaDisabledTooltip = isBuscaDisabled
    ? 'Selecione uma empresa para habilitar a busca em modo grupo'
    : null;

  // A lista de gestores não é mais pré-carregada na sessão (busca sob demanda
  // em GestorSwitchModal/useTrocaGestor) — o botão de trocar gestor fica
  // sempre disponível; se houver só 1 gestor vinculado, o modal mostra isso.

  return (
    <header className="border-b border-border bg-card sticky top-0 z-30 shadow-sm overflow-visible">
      <div
        className={
          compact
            ? 'w-full px-4 py-3 overflow-visible'
            : 'container mx-auto px-6 py-4 overflow-visible'
        }
      >
        <div className="flex items-center justify-between gap-4 flex-wrap overflow-visible">
          {!hideLogo && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Link to="/" className="shrink-0">
                  <div className="h-10 w-[45px] relative overflow-hidden cursor-pointer hover:opacity-80 transition-opacity">
                    <img
                      src="/logo-it-works.png"
                      alt="IT Works"
                      width={50}
                      height={40}
                      className="absolute top-0 left-0 object-cover object-left h-10"
                    />
                  </div>
                </Link>
                <div className="border-l border-border pl-3 ml-1">
                  <h1 className="text-base font-semibold text-foreground">{title || 'TurboPro'}</h1>
                  <p className="text-xs text-muted-foreground">
                    {subtitle || 'Reforma Tributária'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Seletores */}
          <div className={cn('flex items-center gap-3 flex-wrap', hideLogo && 'ml-auto')}>
            {/* Botão Automações (só em /relatorios) */}
            {!compact && isRelatorios && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative mt-5 flex items-center">
                    {isLoadingRobo && (
                      <div className="absolute right-full mr-2 flex items-center">
                        <div className="relative bg-background border border-border rounded-2xl px-2.5 py-1.5 shadow-sm">
                          <span className="flex gap-[3px] items-center">
                            <span className="h-[3px] w-[3px] rounded-full bg-muted-foreground animate-bounce [animation-delay:0ms]" />
                            <span className="h-[3px] w-[3px] rounded-full bg-muted-foreground animate-bounce [animation-delay:150ms]" />
                            <span className="h-[3px] w-[3px] rounded-full bg-muted-foreground animate-bounce [animation-delay:300ms]" />
                          </span>
                          {/* cauda apontando para o robô */}
                          <span className="absolute left-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-l-border" />
                          <span className="absolute left-full top-1/2 -translate-y-1/2 translate-x-[-1px] border-[4px] border-transparent border-l-background" />
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      className={cn(
                        'relative shrink-0 grid place-items-center rounded-full ring-1 h-7 w-7 transition-colors',
                        isLoadingRobo
                          ? 'bg-muted text-muted-foreground ring-border'
                          : botAtivo
                            ? 'bg-teal-50 text-teal-600 ring-teal-200 hover:bg-teal-100'
                            : 'bg-red-50 text-red-400 ring-red-200 hover:bg-red-100'
                      )}
                      aria-label={botAtivo ? 'Robô ativo' : 'Robô inativo'}
                    >
                      <Bot size={19} className="-translate-x-[0.5px] -translate-y-px" />
                      {!isLoadingRobo && !botAtivo && (
                        <span
                          aria-hidden
                          className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
                        >
                          <span className="absolute top-1/2 left-0 w-full h-[1.5px] bg-red-400 -translate-y-1/2 rotate-45 origin-center" />
                        </span>
                      )}
                    </button>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {isLoadingRobo ? 'Verificando robô...' : botAtivo ? 'Robô ativo' : 'Robô inativo'}
                </TooltipContent>
              </Tooltip>
            )}
            {/* Seletor de Empresa (Contribuintes) */}
            <div className="flex flex-col gap-1">
              {!compact && (
                <label className="text-xs text-muted-foreground font-medium">Empresa</label>
              )}
              <div className="flex items-center gap-1.5">
                <Select
                  open={empresaSelectOpen}
                  onOpenChange={setEmpresaSelectOpen}
                  value={cnpjSelecionado}
                  onValueChange={handleEmpresaChange}
                  disabled={isLoadingContribuintes}
                >
                  <SelectTrigger className="w-[280px] h-9 bg-background">
                    <SelectValue
                      placeholder={modoGruposEmpresa ? 'Selecione o grupo' : 'Selecione a empresa'}
                      className="text-foreground placeholder:text-foreground"
                    >
                      {cnpjSelecionado && contribuintes ? (
                        (() => {
                          const selected = contribuintes.find(
                            c => c.CNPJ_Contribuinte === cnpjSelecionado
                          );
                          return selected ? (
                            `${selected.Nome} - ${formatCNPJ(selected.CNPJ_Contribuinte)}`
                          ) : (
                            <span className="text-foreground">
                              {modoGruposEmpresa ? 'Selecione o grupo' : 'Selecione a empresa'}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-foreground">
                          {modoGruposEmpresa ? 'Selecione o grupo' : 'Selecione a empresa'}
                        </span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {/* Campo de busca */}
                    <div className="p-2 border-b">
                      <Input
                        type="text"
                        placeholder="Buscar por nome ou CNPJ..."
                        value={searchEmpresa}
                        onChange={e => setSearchEmpresa(e.target.value)}
                        className="h-8"
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </div>

                    <div className="max-h-[300px] overflow-auto">
                      {isLoadingContribuintes ? (
                        <SelectItem value="loading" disabled>
                          Carregando...
                        </SelectItem>
                      ) : (
                          modoGruposEmpresa
                            ? dropdownEmpresasAgrupadasFiltradas.length > 0
                            : contribuintesFiltrados.length > 0
                        ) ? (
                        modoGruposEmpresa ? (
                          dropdownEmpresasAgrupadasFiltradas.map(item => {
                            if (item.kind === 'single') {
                              const isSelected = item.empresa.CNPJ_Contribuinte === cnpjSelecionado;
                              return (
                                <SelectItem
                                  key={item.empresa.CNPJ_Contribuinte}
                                  value={item.empresa.CNPJ_Contribuinte}
                                  className={cn(
                                    isSelected ? 'bg-accent data-[state=checked]:bg-accent' : '',
                                    '[&>span:first-child]:hidden hover:bg-accent/50 focus:bg-accent/50 focus:text-foreground'
                                  )}
                                >
                                  <div className="flex w-full items-center gap-2">
                                    <span
                                      className={cn(
                                        'inline-flex items-center justify-center h-4 w-4 rounded-full border shrink-0',
                                        isSelected
                                          ? 'border-[#404040]'
                                          : 'border-muted-foreground/50'
                                      )}
                                    >
                                      {isSelected && (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 16 16"
                                          fill="#404040"
                                          className="h-2.5 w-2.5"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </span>
                                    <span className="truncate">
                                      {item.empresa.Nome} -{' '}
                                      {formatCNPJ(item.empresa.CNPJ_Contribuinte)}
                                    </span>
                                  </div>
                                </SelectItem>
                              );
                            }

                            const open = !!gruposEmpresaAbertos[item.groupKey];
                            const isSelected = item.matriz.CNPJ_Contribuinte === cnpjSelecionado;

                            return (
                              <div key={item.groupKey}>
                                <div
                                  className={cn(
                                    'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none',
                                    isSelected ? 'bg-accent' : 'hover:bg-accent/50'
                                  )}
                                >
                                  <div
                                    className="flex min-w-0 flex-1 items-center gap-2 cursor-pointer"
                                    onPointerDown={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const cnpjsCsv = [
                                        item.matriz.CNPJ_Contribuinte,
                                        ...item.vinculadas.map(v => v.CNPJ_Contribuinte),
                                      ].join(',');
                                      handleGrupoChange(item.matriz.CNPJ_Contribuinte, cnpjsCsv);
                                      setEmpresaSelectOpen(false);
                                    }}
                                  >
                                    <span
                                      className={cn(
                                        'inline-flex items-center justify-center h-4 w-4 rounded-full border shrink-0',
                                        isSelected
                                          ? 'border-[#404040]'
                                          : 'border-muted-foreground/50'
                                      )}
                                    >
                                      {isSelected && (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 16 16"
                                          fill="#404040"
                                          className="h-2.5 w-2.5"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </span>
                                    <span className="truncate">
                                      {item.matriz.Nome} -{' '}
                                      {formatCNPJ(item.matriz.CNPJ_Contribuinte)}
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    className="inline-flex shrink-0 items-center justify-center rounded-sm p-1 hover:bg-accent/50"
                                    aria-label={open ? 'Recolher grupo' : 'Expandir grupo'}
                                    onPointerDown={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setGruposEmpresaAbertos(prev => ({
                                        ...prev,
                                        [item.groupKey]: !prev[item.groupKey],
                                      }));
                                    }}
                                  >
                                    <ChevronDown
                                      className={cn(
                                        'h-4 w-4 text-muted-foreground transition-transform',
                                        open ? 'rotate-180' : ''
                                      )}
                                    />
                                  </button>
                                </div>

                                {open && (
                                  <div className="flex flex-col">
                                    {item.vinculadas.map(vinculada => (
                                      <div
                                        key={vinculada.CNPJ_Contribuinte}
                                        className="flex w-full items-center gap-2 py-1.5 pl-8 pr-2 text-sm text-muted-foreground"
                                      >
                                        <span className="h-2 w-2 rounded-full bg-muted-foreground/30 shrink-0" />
                                        <span className="truncate">
                                          {vinculada.Nome} -{' '}
                                          {formatCNPJ(vinculada.CNPJ_Contribuinte)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          contribuintesFiltrados.map(contribuinte => {
                            const isSelected = contribuinte.CNPJ_Contribuinte === cnpjSelecionado;
                            return (
                              <SelectItem
                                key={contribuinte.CNPJ_Contribuinte}
                                value={contribuinte.CNPJ_Contribuinte}
                                className={cn(
                                  isSelected ? 'bg-accent data-[state=checked]:bg-accent' : '',
                                  '[&>span:first-child]:hidden hover:bg-accent/50 focus:bg-accent/50 focus:text-foreground'
                                )}
                              >
                                <div className="flex w-full items-center gap-2">
                                  <span
                                    className={cn(
                                      'inline-flex items-center justify-center h-4 w-4 rounded-full border shrink-0',
                                      isSelected ? 'border-[#404040]' : 'border-muted-foreground/50'
                                    )}
                                  >
                                    {isSelected && (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 16 16"
                                        fill="#404040"
                                        className="h-2.5 w-2.5"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    )}
                                  </span>
                                  <span className="truncate">
                                    {contribuinte.Nome} -{' '}
                                    {formatCNPJ(contribuinte.CNPJ_Contribuinte)}
                                  </span>
                                </div>
                              </SelectItem>
                            );
                          })
                        )
                      ) : searchEmpresa ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          Nenhuma empresa encontrada para "{searchEmpresa}"
                        </div>
                      ) : (
                        <SelectItem value="empty" disabled>
                          Nenhuma empresa disponível
                        </SelectItem>
                      )}
                    </div>
                  </SelectContent>
                </Select>
                {!compact && (
                  <button
                    type="button"
                    disabled={disableModoGrupoEmpresa}
                    className={cn(
                      'relative shrink-0 inline-flex items-center justify-center rounded-md border h-9 w-9 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                      modoGruposEmpresa
                        ? 'border-[#aed561] bg-background text-[#aed561] hover:bg-[#aed561]/15 hover:text-[#8ab83a]'
                        : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                    title={
                      disableModoGrupoEmpresa
                        ? 'Agrupamento de empresas desabilitado nesta tela'
                        : modoGruposEmpresa
                          ? 'Mostrar lista simples'
                          : 'Mostrar por grupos (matriz/filiais)'
                    }
                    aria-label={
                      disableModoGrupoEmpresa
                        ? 'Agrupamento de empresas desabilitado nesta tela'
                        : modoGruposEmpresa
                          ? 'Desativar agrupamento de empresas'
                          : 'Ativar agrupamento de empresas'
                    }
                    onClick={() => {
                      if (disableModoGrupoEmpresa) return;
                      setModoGruposEmpresa(prev => !prev);
                      setCnpjSelecionado('');
                      setEmpresa('');
                      setEmpresaCnpjs(null);
                    }}
                  >
                    <UsersThreeIcon size={22} weight="bold" />
                    {modoGruposEmpresa && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-[#aed561] bg-[#f4fae2]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="#404040"
                          className="h-2.5 w-2.5"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {(!compact || showPeriodo) && (
              <Button
                size="sm"
                className={cn(
                  'bg-accent hover:bg-accent/90 text-accent-foreground',
                  compact ? '' : 'mt-5'
                )}
                onClick={() => {
                  if (disableFiltroPeriodo) return;
                  setShowPeriodoModal(true);
                }}
                disabled={disableFiltroPeriodo}
                title={
                  disableFiltroPeriodo ? 'Filtro de período desabilitado nesta tela' : undefined
                }
              >
                <Calendar className="h-4 w-4 mr-2" />
                Selecionar Período
              </Button>
            )}
            {(!compact || showPeriodo) && !disableFiltroPeriodo && (
              <PeriodoModal
                open={showPeriodoModal}
                onOpenChange={setShowPeriodoModal}
                initialFiltro={periodoFiltro}
                onAplicar={handlePeriodoChange}
              />
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={isBuscaDisabled ? 0 : -1}
                  title={buscaDisabledTooltip ?? undefined}
                  className={cn('inline-flex', compact ? '' : 'mt-5')}
                >
                  <Button
                    size="sm"
                    className="bg-background border shadow-card hover:bg-accent/90 text-accent-foreground cursor-pointer"
                    onClick={() => {
                      if (isBuscaDisabled) return;
                      /* Decisão 11: o recorte passa a valer nas OUTRAS telas a partir deste
                         clique. Marcar antes de buscar, porque quem marca é o ato, não o
                         resultado — e uma consulta que falha não desfaz a escolha. */
                      marcarAplicado();
                      onBuscar?.();
                    }}
                    disabled={isBuscaDisabled}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Buscar
                  </Button>
                </span>
              </TooltipTrigger>
              {buscaDisabledTooltip && (
                <TooltipContent side="bottom">{buscaDisabledTooltip}</TooltipContent>
              )}
            </Tooltip>

            {hasFiltrosAtivos && (
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  'group relative bg-background shadow-card overflow-hidden transition-all duration-100 hover:border-red-600 hover:bg-transparent hover:text-red-600 border w-9 pl-5 hover:w-auto hover:px-4',
                  compact ? '' : 'mt-5'
                )}
                onClick={handleLimparFiltros}
                title="Limpar todos os filtros"
              >
                <div className="flex items-center justify-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  <span className="max-w-0 overflow-hidden opacity-0 group-hover:max-w-xs group-hover:opacity-100 whitespace-nowrap">
                    Limpar Filtros
                  </span>
                </div>
              </Button>
            )}

            {/* Decisão 1 da SPEC-017: "Trocar gestor" existia só como `title` do bloco com o
                nome da empresa, e deixou de ser descoberta por acaso. O bloco da empresa
                continua, porque é ele que DIZ qual gestor está ativo.

                Configurações NÃO fica aqui (decisão 12, que revê a 2): ele já existe no menu
                do usuário, e tê-lo nos dois lugares dava dois caminhos para a mesma tela a um
                palmo de distância. A P.O. pediu de volta como era na versão antiga depois de
                usar a nova em produção. */}
            <div className={cn('flex items-center gap-1', compact ? '' : 'mt-5')}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowGestorModal(true)}
              >
                <ArrowLeftRight className="h-4 w-4" />
                Trocar gestor
              </Button>
            </div>

            {/* Dropdown do Usuário */}
            <div className={cn('flex items-center gap-3', compact ? '' : 'mt-5')}>
              <div className="border-l border-border pl-4 ml-1">
                <div className="flex items-center gap-3">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger className="group h-12 w-12 rounded-full p-0 inline-flex items-center justify-center bg-linear-to-br from-accent/5 to-accent/10 data-[state=closed]:hover:from-accent/10 data-[state=closed]:hover:to-accent/20 border-2 border-transparent data-[state=closed]:hover:border-accent transition-all duration-300 ease-out data-[state=closed]:hover:scale-105 data-[state=closed]:hover:shadow-lg data-[state=closed]:hover:shadow-accent/20 data-[state=open]:border-accent data-[state=open]:from-accent/10 data-[state=open]:to-accent/20 outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2">
                      <User className="h-6 w-6 text-accent transition-transform duration-300 ease-out group-hover:scale-110" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-72 p-2 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
                      sideOffset={12}
                      forceMount
                    >
                      <DropdownMenuLabel className="font-normal px-3 py-3">
                        <div className="flex flex-col space-y-0.5">
                          <p className="text-sm font-semibold text-foreground">
                            {user?.empresa || 'Empresa'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {user?.email || 'email@empresa.com'}
                          </p>
                        </div>
                      </DropdownMenuLabel>
                      <div className="px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent">
                            CNPJ:{' '}
                            {user?.cnpj
                              ? user.cnpj.replace(
                                  /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                                  '$1.$2.$3/$4-$5'
                                )
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem
                        className="cursor-pointer rounded-md mx-1 px-3 py-2"
                        onClick={() => navigate('/configuracoes')}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Configurações
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 rounded-md mx-1 px-3 py-2"
                        onClick={handleLogout}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sair da conta
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    type="button"
                    className="text-left rounded-md px-1 py-1 -mx-1 -my-1 transition-colors cursor-pointer hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                    onClick={() => setShowGestorModal(true)}
                    title="Trocar gestor"
                  >
                    <p className="text-sm font-medium text-foreground leading-tight">
                      {user?.empresa || 'Empresa'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      CNPJ:{' '}
                      {user?.cnpj
                        ? user.cnpj.replace(
                            /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                            '$1.$2.$3/$4-$5'
                          )
                        : 'N/A'}
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <GestorSwitchModal open={showGestorModal} onOpenChange={setShowGestorModal} />
          </div>
        </div>
      </div>
    </header>
  );
}
